<script setup>
/**
 * One period of one budget: how full it is, in words and as a bar.
 *
 * A budget has no due date to report, so the line that would carry one carries
 * the figure instead — the bar is the same thing said as a length, for reading
 * at a glance rather than for reading exactly.
 */
import { computed } from 'vue'
import Icon from './ui/Icon.vue'
import { fmt, focusBudget } from '../stores'
import { openSheet } from '../stores/state'
import { monthName, monthOf, shortDay } from '../lib/dates'
import { cadenceKey } from '../lib/recurrence'
import { budgetLabelKey, budgetTone, isWholeMonth } from '../lib/budgets'
import { t } from '../stores/i18n'

const props = defineProps({ row: { type: Object, required: true } })

const TONE = {
  good: 'bg-accent',
  warn: 'bg-warn',
  danger: 'bg-danger',
  info: 'bg-goal',
  muted: 'bg-line',
}
const TEXT = {
  good: 'text-accent',
  warn: 'text-warn',
  danger: 'text-danger',
  info: 'text-goal',
  muted: 'text-faint',
}

/** Label and colour both come from the status table, so nothing can drift. */
const status = computed(() => ({
  label: t(budgetLabelKey(props.row)),
  tone: budgetTone(props.row),
}))

const period = computed(() =>
  isWholeMonth(props.row)
    ? monthName(monthOf(props.row.from))
    : `${shortDay(props.row.from)} — ${shortDay(props.row.to)}`,
)

const percent = computed(() => Math.round(props.row.progress * 100))

</script>

<template>
  <div class="group flex items-center gap-2.5 px-4 py-2.5 transition hover:bg-raised/50">
    <span class="grid size-8 shrink-0 place-items-center">
      <span class="size-2.5 rounded-full" :class="TONE[status.tone]" />
    </span>

    <button class="min-w-0 flex-1 text-left" @click="focusBudget(row)">
      <span class="flex min-w-0 items-baseline gap-2">
        <span class="truncate text-[14px] font-medium" :title="row.name">{{ row.name }}</span>
        <span class="shrink-0 text-[11px] text-faint">
          {{ row.budget.cadence === 'monthly' ? period : t(cadenceKey(row.budget.cadence)) }}
        </span>
      </span>
      <span class="mt-0.5 block truncate text-[12px]" :class="TEXT[status.tone]">
        <template v-if="row.status === 'learning'">{{ fmt(row.actual) }}</template>
        <template v-else>{{ t('common.xOfY', { x: fmt(row.actual), y: fmt(row.expected) }) }}</template>
        <span v-if="row.autoBasis !== null" class="text-faint">
          · {{ t('budgets.auto', { count: row.autoBasis }) }}
        </span>
      </span>
      <span
        class="mt-1.5 block h-1 w-full overflow-hidden rounded-full bg-line-soft"
        role="progressbar"
        :aria-valuenow="percent"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-label="t('budgets.meter', { category: row.name, percent })"
      >
        <span
          class="block h-full rounded-full transition-[width]"
          :class="TONE[status.tone]"
          :style="{ width: `${Math.max(2, percent)}%` }"
        />
      </span>
    </button>

    <span class="shrink-0 text-right">
      <span class="tnum block text-[14px] font-semibold">{{ fmt(row.expected) }}</span>
      <span class="mt-0.5 block text-[11px]" :class="TEXT[status.tone]">{{ status.label }}</span>
    </span>

    <button
      class="btn-ghost !px-1.5 !py-1"
      :aria-label="t('budgets.editLabel', { category: row.name })"
      @click="openSheet('budget', { id: row.budgetId })"
    >
      <Icon name="pencil" :size="15" />
    </button>
  </div>
</template>
